/**
 * Spec 016 US4 (T049) — the viewer's DSL `game.json` drop-in bridge.
 *
 * The simulation runtime (`run_webgpu` / `run_cpu` / `run_webgpu_metrics`)
 * consumes a compiled `IRModule`, not the DSL. Before US4 the viewer could only
 * ingest an already-compiled `*.ir.json` (plus its `*.plan.json` sidecar). This
 * module closes that gap: it hands a raw `game.json` to the in-WASM
 * `compile_game` surface (`contracts/wasm-boundary.md`) and returns the
 * four-field `{ validation, ir, plan, config_hash }` output, so the viewer can
 * render validation feedback and feed `ir` straight into the runtime — with no
 * server, no IR file, and no `game.layout.json` sidecar (Constitution V: layout
 * is never part of semantics).
 *
 * `compile_game` is **synchronous** in Rust, but we still load the wasm module
 * (`loadWasmModule()`) first — hence the async wrapper. The frontend crates are
 * already linked into the runtime bundle (R2 "always-loaded"), so this reuses
 * the same module the adapters loaded; there is no second module to fetch.
 */

import { loadWasmModule } from "./wasmModule";

/** A single validator issue (one shape for all three severities). */
export interface ValidationIssue {
  /** Stable error code (`REF_UNDEFINED_SYMBOL`, …). */
  code: string;
  severity: "error" | "warning" | "info";
  /** RFC-6901 JSON pointer to the offending site ("" for whole-document). */
  location: string;
  /** One-line description. */
  message: string;
  /** Longer explanation with context. */
  detail: string;
  /** Proposed corrections, sorted by descending confidence. */
  suggested_fixes: SuggestedFix[];
  /** Related JSON pointers. */
  related_locations: string[];
}

/** A machine-applicable correction proposed by the validator. */
export interface SuggestedFix {
  description: string;
  /** RFC-6902 JSON Patch (may be empty). */
  patch: unknown[];
  /** Calibrated confidence in `[0, 1]`. */
  confidence: number;
  notes?: string;
}

/**
 * The validator's structured output. Mirrors the Rust `ValidationResult`
 * (`crates/slot-engine-validator/src/result.rs`) — only the fields the viewer
 * reads are typed precisely; `analysis` / `summary` are passed through opaque.
 */
export interface ValidationReport {
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  info: ValidationIssue[];
  analysis: Record<string, unknown>;
  summary: Record<string, unknown>;
  fingerprint: string;
}

/**
 * The four-field `compile_game` output (`contracts/wasm-boundary.md`).
 *
 * `validation` is always present. `ir` / `plan` / `config_hash` are each
 * non-null **iff** `validation.errors` is empty (produced together, gated
 * identically). The viewer's drop-in path uses only `ir`; `plan` +
 * `config_hash` unlock the large-N GPU-aggregator path (passed verbatim to
 * `run_webgpu_metrics`, exactly like the uploaded `*.plan.json` sidecar).
 */
export interface CompileGameOutput {
  validation: ValidationReport;
  ir: Record<string, unknown> | null;
  /** The Rust wire-form `ExecutionPlan` (the shape `run_webgpu_metrics` takes). */
  plan: Record<string, unknown> | null;
  config_hash: string | null;
}

/**
 * Compile a raw `game.json` source string to the four-field
 * `CompileGameOutput`.
 *
 * Loads the WASM module (memoised) and calls `compile_game`. Never rejects on a
 * *malformed game* — an invalid config resolves to an output whose
 * `validation.errors` is non-empty and whose `ir` / `plan` / `config_hash` are
 * `null`. It rejects only on an *infrastructure* failure: the WASM module not
 * loading, or `compile_game` returning non-JSON (a contract breach).
 */
export async function compileGame(source: string): Promise<CompileGameOutput> {
  const mod = await loadWasmModule();
  const json = mod.compile_game(source);
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (err) {
    throw new Error(
      `compile_game returned non-JSON (contract breach): ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
  if (!isCompileGameOutput(parsed)) {
    throw new Error(
      "compile_game output missing the expected { validation, ir, plan, config_hash } shape",
    );
  }
  return parsed;
}

/** Narrow the parsed JSON to a `CompileGameOutput` (validates only the shape). */
function isCompileGameOutput(value: unknown): value is CompileGameOutput {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  // `validation` is always an object with the three severity arrays; the trio
  // is `object | null` (gated together). We don't deep-validate — the Rust side
  // guarantees the shape — only that the keys exist with plausible types.
  const v = obj.validation;
  if (typeof v !== "object" || v === null) return false;
  const vr = v as Record<string, unknown>;
  if (!Array.isArray(vr.errors)) return false;
  return "ir" in obj && "plan" in obj && "config_hash" in obj;
}
